import React, { useState, useEffect } from 'react';
import { colors, radius, fonts } from '../theme';
import { products } from '../api';
import { IconMenu } from '../components/Icons';

interface CateringProduct {
  id: number;
  name: string;
  description: string;
  price: number;
  catering_category: string;
  image_url: string | null;
  is_available: boolean;
  is_featured: boolean;
  sort_order: number;
}

interface CateringCategory {
  label: string;
  products: CateringProduct[];
}

type CateringMenu = Record<string, CateringCategory>;

interface CartItem { product: CateringProduct; qty: number; }

// Category metadata (emoji + display label + image) for filter tabs and section headers
const categoryMeta: Record<string, { emoji: string; label: string; image: string }> = {
  rotisserie_poulet: { emoji: '🍗', label: 'Rôtisserie Poulet', image: '/traiteur/rotisserie_poulet.jpg' },
  canards_entiers: { emoji: '🦆', label: 'Canards Entiers', image: '/traiteur/canards_entiers.jpg' },
  plats_chauds: { emoji: '🌶️', label: 'Plats Chauds', image: '/traiteur/plats_chauds.jpg' },
  soupes_thai: { emoji: '🍲', label: 'Soupes Thaï', image: '/traiteur/soupes_thai.jpg' },
  tapas: { emoji: '🥟', label: 'Tapas', image: '/traiteur/tapas.jpg' },
  brochettes_satay: { emoji: '🍢', label: 'Brochettes Satay', image: '/traiteur/brochettes_satay.jpg' },
  boulettes: { emoji: '🧆', label: 'Boulettes', image: '/traiteur/boulettes.jpg' },
  verrines: { emoji: '🥂', label: 'Verrines', image: '/traiteur/verrines.jpg' },
  wok_siam: { emoji: '🔥', label: 'Wok Siam', image: '/traiteur/wok_siam.jpg' },
};

export function TraiteurPage() {
  const [menu, setMenu] = useState<CateringMenu | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [showCart, setShowCart] = useState(false);
  const [addedId, setAddedId] = useState<number | null>(null);
  const [orderFeedback, setOrderFeedback] = useState<number | null>(null);

  useEffect(() => {
    products.cateringMenu()
      .then((res: any) => {
        const data = res.data?.menu || res.data || {};
        setMenu(data);
        setLoading(false);
      })
      .catch((err: any) => {
        setError('Impossible de charger le menu traiteur');
        setLoading(false);
      });
  }, []);

  const addToCart = (p: CateringProduct) => {
    setCart(prev => {
      const existing = prev.find(c => c.product.id === p.id);
      if (existing) return prev.map(c => c.product.id === p.id ? { ...c, qty: c.qty + 1 } : c);
      return [...prev, { product: p, qty: 1 }];
    });
    setAddedId(p.id);
    setTimeout(() => setAddedId(null), 1200);
  };

  const removeFromCart = (id: number) => setCart(prev => prev.filter(c => c.product.id !== id));

  const cartTotal = cart.reduce((s, c) => s + c.product.price * c.qty, 0);
  const cartCount = cart.reduce((s, c) => s + c.qty, 0);

  const handleOrder = (productId: number) => {
    // Find product in menu and add to cart
    if (menu) {
      for (const catKey of Object.keys(menu)) {
        const cat = menu[catKey];
        const product = cat.products?.find((p: CateringProduct) => p.id === productId);
        if (product) {
          addToCart(product);
          return;
        }
      }
    }
    setOrderFeedback(productId);
    setTimeout(() => setOrderFeedback(null), 1500);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <div style={{ color: colors.gold, fontSize: 20, fontFamily: fonts.body }}>Chargement du menu traiteur...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <div style={{ color: colors.error, fontSize: 16, fontFamily: fonts.body }}>{error}</div>
      </div>
    );
  }

  const categoryKeys = menu ? Object.keys(menu) : [];
  const filteredKeys = activeFilter === 'all' ? categoryKeys : categoryKeys.filter(k => k === activeFilter);

  return (
    <div>
      {/* Header + Cart button */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: colors.gold, margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <IconMenu /> Menu Traiteur
          </h1>
          <p style={{ color: colors.textMuted, fontSize: 14, margin: 0, fontFamily: fonts.body }}>
            Nos plats traiteur premium fusion thaï-sichuanaise — commandez vos favoris
          </p>
        </div>
        <button
          onClick={() => setShowCart(!showCart)}
          style={{
            position: 'relative', padding: '10px 20px', borderRadius: 24,
            background: 'rgba(201,169,110,0.15)', border: '1px solid rgba(201,169,110,0.3)',
            color: colors.gold, cursor: 'pointer', fontWeight: 600, fontSize: 14,
            fontFamily: fonts.body, transition: 'all 0.2s',
          }}
        >
          🛒 Panier {cartCount > 0 && <span style={{ background: '#E74C3C', color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: 11, marginLeft: 4 }}>{cartCount}</span>}
        </button>
      </div>

      {/* Cart Panel */}
      {showCart && cart.length > 0 && (
        <div style={{
          background: '#1A1A3A', borderRadius: radius.lg, padding: 20, marginBottom: 24,
          border: '1px solid #2A2A4A',
        }}>
          <h3 style={{ color: colors.gold, margin: '0 0 12px 0' }}>🛒 Votre panier traiteur</h3>
          {cart.map(c => (
            <div key={c.product.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #2A2A4A' }}>
              <div>
                <span style={{ color: '#F5F5F5', fontWeight: 600 }}>{c.product.name}</span>
                <span style={{ color: '#A0A0A0', marginLeft: 8 }}>x{c.qty}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ color: colors.gold, fontWeight: 700 }}>{(c.product.price * c.qty).toFixed(2)}€</span>
                <button onClick={() => removeFromCart(c.product.id)} style={{ background: 'none', border: 'none', color: '#E74C3C', cursor: 'pointer', fontSize: 16 }}>✕</button>
              </div>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, paddingTop: 12, borderTop: `2px solid ${colors.gold}` }}>
            <span style={{ color: '#F5F5F5', fontWeight: 700, fontSize: 16 }}>Total</span>
            <span style={{ color: colors.gold, fontWeight: 700, fontSize: 18 }}>{cartTotal.toFixed(2)}€</span>
          </div>
          <button style={{
            width: '100%', marginTop: 16, padding: 12, borderRadius: radius.md,
            background: `linear-gradient(135deg, ${colors.gold}, #B8860B)`, border: 'none',
            color: '#0F0F23', fontWeight: 700, fontSize: 15, cursor: 'pointer',
            fontFamily: fonts.body,
          }}>
            Commander ({cartCount} article{cartCount > 1 ? 's' : ''})
          </button>
        </div>
      )}

      {/* Category Filter Tabs */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 28 }}>
        <button
          onClick={() => setActiveFilter('all')}
          style={{
            padding: '8px 18px', borderRadius: 24, border: 'none', cursor: 'pointer',
            background: activeFilter === 'all' ? colors.gold : 'rgba(201,169,110,0.15)',
            color: activeFilter === 'all' ? '#0F0F23' : colors.gold,
            fontWeight: activeFilter === 'all' ? 700 : 400, fontSize: 13,
            fontFamily: fonts.body, transition: 'all 0.2s',
          }}
        >
          ✨ Toutes
        </button>
        {categoryKeys.map(key => {
          const meta = categoryMeta[key] || { emoji: '🍽️', label: key, image: '' };
          const active = activeFilter === key;
          return (
            <button
              key={key}
              onClick={() => setActiveFilter(key)}
              style={{
                padding: '8px 18px', borderRadius: 24, border: 'none', cursor: 'pointer',
                background: active ? colors.gold : 'rgba(201,169,110,0.15)',
                color: active ? '#0F0F23' : colors.gold,
                fontWeight: active ? 700 : 400, fontSize: 13,
                fontFamily: fonts.body, transition: 'all 0.2s',
              }}
            >
              {meta.emoji} {meta.label}
            </button>
          );
        })}
      </div>

      {/* Sections by Category */}
      {filteredKeys.map(catKey => {
        const cat = menu![catKey];
        const meta = categoryMeta[catKey] || { emoji: '🍽️', label: catKey };
        const catProducts = cat.products || [];

        return (
          <div key={catKey} style={{ marginBottom: 36 }}>
            {/* Category hero image */}
            {meta.image && (
              <div style={{
                width: '100%', height: 200, borderRadius: radius.lg, overflow: 'hidden',
                marginBottom: 16, position: 'relative',
              }}>
                <img
                  src={meta.image}
                  alt={meta.label}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  background: 'linear-gradient(transparent, rgba(15,15,35,0.92))',
                  padding: '20px 24px 16px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 28 }}>{meta.emoji}</span>
                    <h2 style={{ color: colors.gold, fontSize: 22, fontWeight: 700, margin: 0, fontFamily: fonts.body }}>
                      {meta.label}
                    </h2>
                    <span style={{ color: colors.textMuted, fontSize: 12, fontFamily: fonts.body, marginLeft: 8 }}>
                      {catProducts.length} spécialité{catProducts.length > 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              </div>
            )}
            {/* Section Header fallback if no image */}
            {!meta.image && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12,
                borderBottom: `2px solid ${colors.gold}`, paddingBottom: 10, marginBottom: 18,
              }}>
                <span style={{ fontSize: 24 }}>{meta.emoji}</span>
                <h2 style={{ color: colors.gold, fontSize: 20, fontWeight: 700, margin: 0, fontFamily: fonts.body }}>
                  {meta.label}
                </h2>
                <span style={{ color: colors.textMuted, fontSize: 12, fontFamily: fonts.body, marginLeft: 8 }}>
                  {catProducts.length} spécialité{catProducts.length > 1 ? 's' : ''}
                </span>
              </div>
            )}

            {/* Product Cards Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
              {catProducts.map((product: CateringProduct) => (
                <div
                  key={product.id}
                  style={{
                    background: colors.card, borderRadius: radius.lg, overflow: 'hidden',
                    border: '1px solid #2A2A4A', transition: 'transform 0.2s, box-shadow 0.2s',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 12px 32px rgba(201,169,110,0.2)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {/* Product card top bar with category accent */}
                  <div style={{
                    height: 6,
                    background: `linear-gradient(90deg, ${colors.gold}, #B8860B)`,
                  }} />

                  {/* Product image */}
                  {product.image_url && (
                    <div style={{ width: '100%', height: 140, overflow: 'hidden' }}>
                      <img
                        src={product.image_url}
                        alt={product.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </div>
                  )}

                  <div style={{ padding: '16px 18px 18px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                      <h3 style={{ color: '#F5F5F5', fontSize: 15, fontWeight: 700, margin: 0, flex: 1 }}>
                        {product.name}
                      </h3>
                      {product.is_featured && (
                        <span style={{
                          background: colors.gold, color: '#0F0F23',
                          padding: '2px 8px', borderRadius: 10,
                          fontSize: 10, fontWeight: 700, marginLeft: 8, whiteSpace: 'nowrap',
                        }}>
                          ⭐ Signature
                        </span>
                      )}
                    </div>

                    <p style={{ color: '#A0A0A0', fontSize: 12, margin: '0 0 14px', lineHeight: 1.5, minHeight: product.description ? 36 : 0 }}>
                      {product.description}
                    </p>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: colors.gold, fontSize: 22, fontWeight: 700 }}>
                        {product.price.toFixed(2)}€
                      </span>
                      <button
                        onClick={() => handleOrder(product.id)}
                        style={{
                          padding: '8px 20px', borderRadius: 24,
                          background: addedId === product.id
                            ? '#27AE60'
                            : `linear-gradient(135deg, ${colors.gold}, #B8860B)`,
                          border: 'none',
                          color: addedId === product.id ? '#fff' : '#0F0F23',
                          fontWeight: 700, fontSize: 12, cursor: 'pointer',
                          fontFamily: fonts.body, transition: 'all 0.3s',
                        }}
                      >
                        {addedId === product.id ? '✓ Ajouté !' : 'Ajouter au panier'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Info footer */}
      <div style={{
        background: 'rgba(201,169,110,0.08)', borderRadius: radius.md, padding: 20,
        border: '1px solid rgba(201,169,110,0.2)', marginTop: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <span style={{ fontSize: 20 }}>📞</span>
          <span style={{ color: colors.gold, fontWeight: 600, fontSize: 14, fontFamily: fonts.body }}>
            Commandes traiteur
          </span>
        </div>
        <p style={{ color: colors.textMuted, fontSize: 13, margin: 0, lineHeight: 1.5, fontFamily: fonts.body }}>
          Pour commander nos plats traiteur, contactez-nous par téléphone ou via l'application.
          Commande minimale 24h à l'avance pour les commandes traiteur.
          Livraison disponible sur Saint-Martin.
        </p>
      </div>
    </div>
  );
}